// FLAGS: none
// View model for a hosted private space listed in the expert's own consults table on My Sessions.
// Carries no client identity: a private space belongs to an employer, not to a named client, which
// is why the Client column shows the employer display name instead.
import { ResponseConveneHostedSessionResponse } from "@src/api/v1/data-contracts";

export type PrivateSpaceStatus = "Scheduled" | "Completed" | "Cancelled";

// The API already resolves this, so it is read rather than re-derived. `ConveneSessionStatus` has
// only Scheduled and Cancelled, but the response mapper widens it at the boundary —
// `HostedStatusToString` returns "cancelled", else "completed"/"scheduled" from
// `EndsAt < DateTime.UtcNow` — so the wire value is already the one to show. Recomputing that
// comparison here would duplicate a rule the backend owns (docs/shared/rules.md
// single-source-of-truth) and let the two drift.
//
// Values arrive lowercase; the Lokalise keys are PascalCase. An unrecognised status reads as
// Scheduled, matching the API's own fallback, so a new member lists rather than blanking the cell.
export const getPrivateSpaceStatus = (
  session: Pick<ResponseConveneHostedSessionResponse, "status">,
): PrivateSpaceStatus => {
  switch ((session.status ?? "").toLowerCase()) {
    case "cancelled":
      return "Cancelled";
    case "completed":
      return "Completed";
    default:
      return "Scheduled";
  }
};

// `eventType` is lowercase on the wire and `ConveneEventType` is a closed set of two. The Type
// column names the audience first and the format second, because every row this feed can return is
// company-owned: `HostedSessionsFor` reads `PrivateSessions()`, so a public masterclass cannot
// appear here. Labelling a row "Masterclass" alone would read as the public format it is not.
//
// An unrecognised value falls back to the group label rather than throwing, so a third event type
// still lists instead of breaking the expert's page. Add the member here when one arrives.
// These two keys are already registered in Lokalise ("Private Group Session", "Private
// Masterclass") and were reachable only from the abandoned unified-list components, so reusing
// them adds no key to register and leaves no window where the column renders a raw key.
export const PRIVATE_SESSION_TYPE_KEYS: Record<string, string> = {
  groupsession: "MySessions.SessionType.PrivateGroupSession",
  masterclass: "MySessions.SessionType.PrivateMasterclass",
};

export const getPrivateSessionTypeKey = (eventType: string): string =>
  PRIVATE_SESSION_TYPE_KEYS[(eventType ?? "").toLowerCase()] ??
  PRIVATE_SESSION_TYPE_KEYS.groupsession;

// Hosted spaces only distinguish scheduled / completed / all, while the consult status pills are
// finer (missed by client, cancelled by OpenUp, and so on). `undefined` means this table's status
// has no space equivalent, so the caller lists consults alone rather than dragging in every space
// under the `all` facet, which is what would silently mix cancelled 1:1s with live spaces.
//
// The All Sessions tab is the empty string, not "all": `statusFilters` in SessionFilters gives that
// pill `id: ""` and casts it to ModelsConsultStatus, so "" is a real runtime value the enum does
// not list. Missing that is what kept every space off the All Sessions tab. The API agrees on this
// spelling — ParseHostedStatus treats a zero-length status as All — so "" and "all" are one facet.
export const getSpaceStatusFacet = (status?: string): string | undefined => {
  const facet = (status ?? "").toLowerCase();

  if (facet === "" || facet === "all") return "all";
  if (facet === "scheduled") return "scheduled";
  if (facet === "completed") return "completed";

  return undefined;
};

export interface PrivateSpaceRow {
  sessionId: string;
  /** Shown in the Client column: a private space is owned by an employer, not a client. */
  employerName: string;
  title: string;
  /** i18n key for the Type column, e.g. "Private Session (Group)". */
  typeLabelKey: string;
  status: PrivateSpaceStatus;
  startsAt: string;
  timeZone: string;
  /**
   * Locale code the session is delivered in, e.g. "nl-NL" — the event's own source language, not
   * one it was translated into. Absent for events created before OI-25305, which declared none;
   * the row then shows no flag rather than defaulting to one, since guessing the language an
   * expert is about to speak is worse than saying nothing.
   */
  languageCode?: string;
  /** Whether the expert may request a start link right now; gates the join action. */
  canStart: boolean;
}

export const mapHostedSessionToPrivateSpaceRow = (
  session: ResponseConveneHostedSessionResponse,
): PrivateSpaceRow => ({
  sessionId: session.sessionId,
  employerName: session.employerName,
  title: session.title,
  typeLabelKey: getPrivateSessionTypeKey(session.eventType),
  status: getPrivateSpaceStatus(session),
  startsAt: session.startsAt,
  timeZone: session.timeZone,
  // Normalised to undefined so the row has one absent case, not both null and undefined, and the
  // renderer can gate on a single truthiness check.
  languageCode: session.languageCode ?? undefined,
  canStart: session.canStart,
});
